import { EConfigurationNames } from '@ConfigLoader/ConfigFiles';
import ConfigLoader from '@ConfigLoader/ConfigLoader';
import { TEntries } from '@GFTypes/Entries';
import EventsCenter from '@Managers/Events/EventsCenter';
import { EGameStoreEvents } from '@Managers/Events/GameStoreEvents';
import { deepMerge, isObjectAndNotArray } from '@Tools/DeepMerge';
import { GAME_STORE_DEFAULTS } from './GameStoreDefaults';
import { EGameStoreKeys, IGameStoreSchema } from './GameStoreSchema';
import { transformUpdateToUILayer } from './GameStoreToUILayerTransform';
import DataManager from '../DataManager/DataManager';
import { DATA_MANAGER_EVENTS } from '../DataManager/DataManagerEvents';

export type TOnChangeCallback<K extends EGameStoreKeys> = (context: any, value: IGameStoreSchema[K], previousValue: IGameStoreSchema[K]) => any;

export class GameStore {
    private readonly registry: DataManager;
    constructor(registry: DataManager) {
        this.registry = registry;
        this.populateDefaults();
    }

    private populateDefaults(): void {
        (Object.entries(GAME_STORE_DEFAULTS) as TEntries<typeof GAME_STORE_DEFAULTS>).forEach(([key, value]) => {
            this.set(key, value);
        });

        this.setDataFromConfiguration();
    }

    private setDataFromConfiguration(): void {
        const gameConfig = ConfigLoader.getConfiguration(EConfigurationNames.GAME);
        const soundConfig = ConfigLoader.getConfiguration(EConfigurationNames.SOUNDS);
        this.set(EGameStoreKeys.AutoPlayState, gameConfig.autoPlaySettings, true);
        this.set(EGameStoreKeys.FreeGamesAutoPlayState, gameConfig.autoPlaySettings, true);
        this.set(EGameStoreKeys.EnvironmentConfiguration, gameConfig.environment);
        this.set(EGameStoreKeys.PreloaderConfiguration, gameConfig.preloader);
        this.set(EGameStoreKeys.SymbolConfiguration, gameConfig.symbols);
        this.set(EGameStoreKeys.WinConfiguration, gameConfig.win);
        this.set(EGameStoreKeys.SpinParametersSets, gameConfig.spinParametersSets);
        this.set(EGameStoreKeys.SpinParameters, gameConfig.spinParametersSets[0]);
        this.set(EGameStoreKeys.WinLevelsConfiguration, gameConfig.winLevels);
        this.set(EGameStoreKeys.WintabConfiguration, gameConfig.winTab);
        this.set(EGameStoreKeys.WildWheelConfiguration, gameConfig.wildWheel);
        this.set(EGameStoreKeys.SoundsConfiguration, { sounds: { ...soundConfig.sharedSounds, ...soundConfig.gameSounds } });
        this.set(EGameStoreKeys.LocalStorageConfiguration, ConfigLoader.getLocalStorageConfiguration(), true);
        this.set(EGameStoreKeys.GambleConfiguration, gameConfig.gamble);
        this.set(EGameStoreKeys.TurboModeState, gameConfig.turbo, true);
        this.set(EGameStoreKeys.BeforeWinAnimationConfiguration, gameConfig.beforeWinAnimation, true);
    }

    public set<K extends EGameStoreKeys>(key: K, value: Partial<IGameStoreSchema[K]>, merge?: boolean): void {
        if (merge) {
            const currentValue = this.get(key);
            if (isObjectAndNotArray(currentValue) && isObjectAndNotArray(value)) value = deepMerge(currentValue, value);
        }
        this.registry.set(key, value);
        EventsCenter.emit(EGameStoreEvents.GAME_STORE_UPDATED, { payload: transformUpdateToUILayer(this.registry.getAll() as IGameStoreSchema), key });
    }

    public get<K extends EGameStoreKeys>(key: K | EGameStoreKeys[]): IGameStoreSchema[K] {
        return this.registry.get(key);
    }

    public on<K extends EGameStoreKeys>(key: K, callback: TOnChangeCallback<K>, context?: any): void {
        this.registry.events.on(`${DATA_MANAGER_EVENTS.CHANGE_DATA_KEY}${key}`, callback, context);
    }

    public off<K extends EGameStoreKeys>(key: K, callback: TOnChangeCallback<K>, context?: any): void {
        this.registry.events.off(`${DATA_MANAGER_EVENTS.CHANGE_DATA_KEY}${key}`, callback, context);
    }

    public merge(data: Partial<{ [key in EGameStoreKeys]: IGameStoreSchema[key] }>, overwrite: boolean = true): void {
        this.registry.merge(data, overwrite);

        EventsCenter.emit(EGameStoreEvents.GAME_STORE_UPDATED, { payload: transformUpdateToUILayer(this.registry.getAll() as IGameStoreSchema) });
    }
}
